/**
 * Formats a license plate to nnn-nn-nnn format
 * @param input - Raw input string
 * @returns Formatted license plate string
 */
export function formatLicensePlate(input: string): string {
  // Remove all non-digit characters
  const digitsOnly = input.replace(/\D/g, '');
  
  // Limit to 8 digits maximum
  const limitedDigits = digitsOnly.slice(0, 8);
  
  // Apply formatting based on length
  if (limitedDigits.length >= 6) {
    // Format as nnn-nn-nnn
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 5)}-${limitedDigits.slice(5, 8)}`;
  } else if (limitedDigits.length >= 4) {
    // Format as nnn-nn
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
  } else if (limitedDigits.length >= 1) {
    // Format as nnn
    return limitedDigits;
  }
  
  return '';
}

/**
 * Formats a phone number to E.164 format with visual formatting
 * @param input - Raw input string
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(input: string): string {
  // Remove all non-digit characters except +
  let cleaned = input.replace(/[^\d+]/g, '');
  
  // Ensure it starts with +
  if (!cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }
  
  // Remove any + signs that aren't at the beginning
  cleaned = '+' + cleaned.substring(1).replace(/\+/g, '');
  
  // Limit to reasonable length (E.164 max is 15 digits + country code)
  if (cleaned.length > 16) {
    cleaned = cleaned.slice(0, 16);
  }
  
  // Basic formatting for common patterns
  if (cleaned.length >= 2) {
    const digits = cleaned.substring(1); // Remove the +
    
    // Israeli number format: +972 50 123 4567
    if (cleaned.startsWith('+972') && digits.length >= 3) {
      if (digits.length >= 10) {
        return `+972 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 12)}`;
      } else if (digits.length >= 8) {
        return `+972 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
      } else if (digits.length >= 5) {
        return `+972 ${digits.slice(3, 5)} ${digits.slice(5)}`;
      } else {
        return `+972 ${digits.slice(3)}`;
      }
    }
    
    // US number format: +1 (555) 123-4567
    if (cleaned.startsWith('+1') && digits.length >= 1) {
      if (digits.length >= 10) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
      } else if (digits.length >= 7) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      } else if (digits.length >= 4) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4)}`;
      } else {
        return `+1 (${digits.slice(1)}`;
      }
    }
    
    // Generic international format: +XX XXX XXX XXXX
    if (digits.length >= 8) {
      const countryCode = digits.slice(0, 2);
      const remaining = digits.slice(2);
      if (remaining.length >= 6) {
        return `+${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3, 6)} ${remaining.slice(6)}`;
      } else if (remaining.length >= 3) {
        return `+${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3)}`;
      } else {
        return `+${countryCode} ${remaining}`;
      }
    }
  }
  
  return cleaned;
}

/**
 * Extracts raw phone number in E.164 format (no formatting)
 * @param formatted - Formatted phone number string
 * @returns Clean E.164 format phone number
 */
export function getCleanPhoneNumber(formatted: string): string {
  // Remove all non-digit characters except +
  let cleaned = formatted.replace(/[^\d+]/g, '');
  
  // Ensure it starts with +
  if (!cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }
  
  // Remove any + signs that aren't at the beginning
  cleaned = '+' + cleaned.substring(1).replace(/\+/g, '');
  
  return cleaned;
}

/**
 * Extracts raw license plate (digits only)
 * @param formatted - Formatted license plate string
 * @returns Clean digits-only license plate
 */
export function getCleanLicensePlate(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

/**
 * Validates if a license plate is in correct format
 * @param plate - License plate string
 * @returns True if valid format
 */
export function isValidLicensePlate(plate: string): boolean {
  return /^\d{3}-\d{2}-\d{3}$/.test(plate);
}

/**
 * Validates if a phone number is in valid E.164 format
 * @param phone - Phone number string
 * @returns True if valid format
 */
export function isValidPhoneNumber(phone: string): boolean {
  const clean = getCleanPhoneNumber(phone);
  return /^\+[1-9]\d{1,14}$/.test(clean);
}